import { Link, useLoaderData } from '@tanstack/react-router';
import {
    ChevronLeft,
    Clapperboard,
    CloudSun,
    LayoutDashboard,
    Mail,
    Menu,
    Plus,
    Send,
    Search,
    X,
} from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect, useId, useRef, useState } from 'react';

import { NewArticleButton } from '@/components/admin/new-article-button';
import { NewsletterSignup } from '@/components/site/newsletter-signup';
import {
    Dialog,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
} from '@/components/ui/dialog';
import { staffAdminHomePath, type StaffRole } from '@/lib/admins';
import { CINEMA_PUBLIC_ENABLED } from '@/lib/cinema-providers';
import { buildSiteNavItems } from '@/lib/site-nav';
import { SITE_SOCIAL_LINKS } from '@/lib/site-social';
import { cn } from '@/lib/utils';

function useSiteNavItems() {
    const { navCategories } = useLoaderData({ from: '__root__' });
    return buildSiteNavItems(navCategories ?? []);
}

export function Cross({ className }: { className?: string }) {
    return (
        <span
            aria-hidden
            className={cn('pointer-events-none absolute z-10 text-white/25', className)}
        >
            +
        </span>
    );
}

export function Cell({
    className,
    crosses = true,
    children,
}: {
    className?: string;
    crosses?: boolean;
    children: React.ReactNode;
}) {
    return (
        <section
            className={cn(
                'relative mx-auto w-full max-w-7xl border-x border-b border-white/10',
                className,
            )}
        >
            {crosses && (
                <>
                    <Cross className="-bottom-2.5 -start-[5px]" />
                    <Cross className="-bottom-2.5 -end-[5px]" />
                </>
            )}
            {children}
        </section>
    );
}

function NavLink({
    href,
    label,
    active,
    onClick,
}: {
    href: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
}) {
    const className = cn(
        'text-sm transition-colors',
        active ? 'text-white' : 'text-white/55 hover:text-white',
    );

    if (href === '/') {
        return (
            <Link to="/" className={className} onClick={onClick}>
                {label}
            </Link>
        );
    }

    const match = href.match(/^\/category\/(.+)$/);
    if (match) {
        return (
            <Link
                to="/category/$key"
                params={{ key: match[1] }}
                className={className}
                onClick={onClick}
            >
                {label}
            </Link>
        );
    }

    return (
        <Link to={href as '/about'} className={className} onClick={onClick}>
            {label}
        </Link>
    );
}

function headerIconClass(active?: boolean) {
    return cn(
        'inline-flex size-10 shrink-0 items-center justify-center border border-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:size-9',
        active ? 'border-white/25 bg-white/[0.06] text-white' : 'text-white/55 hover:text-white',
    );
}

const MOBILE_MENU_LINK =
    'flex min-h-12 items-center justify-between gap-3 px-5 text-[15px] transition-colors active:bg-white/[0.04]';

const MOBILE_DISCOVER_LINKS = [
    { id: 'weather', label: 'מזג האוויר', href: '/weather' as const, icon: CloudSun },
    ...(CINEMA_PUBLIC_ENABLED
        ? [{ id: 'cinema' as const, label: 'קולנוע', href: '/cinema' as const, icon: Clapperboard }]
        : []),
    { id: 'newsletter', label: 'ניוזלטר', href: '/newsletter' as const, icon: Mail },
    { id: 'tip', label: 'שלחו ידיעה', href: '/tip' as const, icon: Send },
    { id: 'search', label: 'חיפוש', href: '/search' as const, icon: Search },
] as const;

function MobileNavItem({
    label,
    active,
    onNavigate,
    icon: Icon,
    ...link
}: {
    label: string;
    active?: boolean;
    onNavigate: () => void;
    icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
} & (
    | { to: '/' }
    | { to: '/category/$key'; params: { key: string } }
    | { to: '/weather' | '/cinema' | '/newsletter' | '/tip' | '/search' | '/about' | '/accessibility' | '/admin' | '/admin/articles' }
)) {
    const className = cn(
        MOBILE_MENU_LINK,
        active
            ? 'border-s-2 border-s-[var(--brand)] bg-white/[0.04] text-white'
            : 'border-s-2 border-s-transparent text-white/70 hover:text-white',
    );

    const content = (
        <>
            <span className="flex min-w-0 flex-1 items-center gap-3 text-start">
                {Icon ? <Icon className="size-4 shrink-0 opacity-55" aria-hidden /> : null}
                {label}
            </span>
            <ChevronLeft className="size-4 shrink-0 opacity-40" aria-hidden />
        </>
    );

    if (link.to === '/category/$key') {
        return (
            <Link
                to="/category/$key"
                params={link.params}
                className={className}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
            >
                {content}
            </Link>
        );
    }

    return (
        <Link
            to={link.to}
            className={className}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
        >
            {content}
        </Link>
    );
}

function MobileNavMenu({
    open,
    onOpenChange,
    activePath,
    canAccessAdmin,
    staffRole,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    activePath?: string;
    canAccessAdmin: boolean;
    staffRole: StaffRole | null;
}) {
    const titleId = useId();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const close = () => onOpenChange(false);
    const navItems = useSiteNavItems();

    useEffect(() => {
        if (!open) return;
        const media = window.matchMedia('(min-width: 1024px)');
        const onChange = () => {
            if (media.matches) onOpenChange(false);
        };
        onChange();
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, [open, onOpenChange]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogOverlay className="bg-black/80 lg:hidden" />
                <DialogPrimitive.Content
                    id="mobile-site-nav"
                    aria-labelledby={titleId}
                    dir="rtl"
                    onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        closeButtonRef.current?.focus();
                    }}
                    className={cn(
                        'fixed inset-0 z-50 flex h-dvh w-full flex-col bg-black outline-none lg:hidden',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
                        'duration-200',
                    )}
                >
                    <DialogTitle id={titleId} className="sr-only">
                        תפריט ניווט
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        מדורים וקישורים באתר אשקלון ניוז
                    </DialogDescription>

                    <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
                        <Link
                            to="/"
                            className="inline-flex shrink-0 items-center gap-3"
                            onClick={close}
                        >
                            <img
                                src="/images/logo.svg"
                                alt="אשקלון ניוז"
                                className="h-8 w-auto"
                                width={120}
                                height={32}
                            />
                            <span
                                aria-hidden
                                className="h-4 w-px shrink-0 bg-white/15"
                            />
                            <span className="text-[11px] tracking-wide text-white/55">
                                לדעת הכל באשקלון
                            </span>
                        </Link>
                        <button
                            ref={closeButtonRef}
                            type="button"
                            className={headerIconClass()}
                            aria-label="סגור תפריט"
                            onClick={close}
                        >
                            <X className="size-4" aria-hidden />
                        </button>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        <nav aria-label="מדורים">
                            <p className="border-b border-white/10 px-5 py-3 text-xs tracking-wide text-white/55">
                                מדורים
                            </p>
                            <ul>
                                {navItems.map((item, index) => {
                                    const match = item.href.match(/^\/category\/(.+)$/);
                                    const active = activePath === item.href;
                                    return (
                                        <li
                                            key={item.id}
                                            className={cn(
                                                index < navItems.length - 1 &&
                                                    'border-b border-white/10',
                                            )}
                                        >
                                            {match ? (
                                                <MobileNavItem
                                                    to="/category/$key"
                                                    params={{ key: match[1] }}
                                                    label={item.label}
                                                    active={active}
                                                    onNavigate={close}
                                                />
                                            ) : (
                                                <MobileNavItem
                                                    to="/"
                                                    label={item.label}
                                                    active={active}
                                                    onNavigate={close}
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>

                        <nav className="border-t border-white/10" aria-label="גלה">
                            <p className="border-b border-white/10 px-5 py-3 text-xs tracking-wide text-white/55">
                                גלה
                            </p>
                            <ul>
                                {MOBILE_DISCOVER_LINKS.map((item, index) => (
                                    <li
                                        key={item.id}
                                        className={cn(
                                            index < MOBILE_DISCOVER_LINKS.length - 1 &&
                                                'border-b border-white/10',
                                        )}
                                    >
                                        <MobileNavItem
                                            to={item.href}
                                            label={item.label}
                                            icon={item.icon}
                                            active={activePath === item.href}
                                            onNavigate={close}
                                        />
                                    </li>
                                ))}
                                <li className="border-t border-white/10">
                                    <MobileNavItem
                                        to="/about"
                                        label="אודות"
                                        active={activePath === '/about'}
                                        onNavigate={close}
                                    />
                                </li>
                                <li className="border-t border-white/10">
                                    <MobileNavItem
                                        to="/accessibility"
                                        label="הצהרת נגישות"
                                        active={activePath === '/accessibility'}
                                        onNavigate={close}
                                    />
                                </li>
                                {canAccessAdmin ? (
                                    <li className="border-t border-white/10">
                                        <MobileNavItem
                                            to={staffAdminHomePath(staffRole)}
                                            label="ניהול"
                                            icon={LayoutDashboard}
                                            active={Boolean(activePath?.startsWith('/admin'))}
                                            onNavigate={close}
                                        />
                                    </li>
                                ) : null}
                            </ul>
                        </nav>

                        <div className="border-t border-white/10 px-5 py-6">
                            <p className="text-xs tracking-wide text-white/55">עקבו אחרינו</p>
                            <ul className="mt-3 flex flex-wrap gap-2" aria-label="רשתות חברתיות">
                                {SITE_SOCIAL_LINKS.map((link) => {
                                    const Icon = link.icon;
                                    return (
                                        <li key={link.id}>
                                            <a
                                                href={link.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={link.label}
                                                className="inline-flex size-10 items-center justify-center border border-white/10 text-white/55 transition-colors hover:border-white/25 hover:text-white"
                                            >
                                                <Icon aria-hidden className="size-4" />
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>
        </Dialog>
    );
}

export function GridNav({ activePath }: { activePath?: string }) {
    const [open, setOpen] = useState(false);
    const {
        canAccessAdmin = false,
        staffRole = null,
        navCategories,
    } = useLoaderData({ from: '__root__' });
    const navItems = useSiteNavItems();

    return (
        <header className="sticky top-0 z-40 border-b border-white/10 bg-black/85 backdrop-blur-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between border-x border-white/10 px-4 sm:px-6">
                <Link to="/" className="inline-flex shrink-0 items-center gap-3">
                    <img
                        src="/images/logo.svg"
                        alt="אשקלון ניוז"
                        className="h-8 w-auto"
                        width={120}
                        height={32}
                    />
                    <span
                        aria-hidden
                        className="hidden h-4 w-px shrink-0 bg-white/15 sm:block"
                    />
                    <span className="hidden text-[11px] tracking-wide text-white/55 sm:inline">
                        לדעת הכל באשקלון
                    </span>
                </Link>

                <div className="flex items-stretch">
                    <nav className="hidden items-stretch lg:flex" aria-label="ניווט ראשי">
                        {navItems.map((item, index) => (
                            <div
                                key={item.id}
                                className={cn(
                                    'flex items-center px-3.5',
                                    index > 0 && 'border-s border-white/10',
                                )}
                            >
                                <NavLink
                                    href={item.href}
                                    label={item.label}
                                    active={activePath === item.href}
                                />
                            </div>
                        ))}
                    </nav>

                    <div className="ms-2 flex items-stretch gap-0">
                        {canAccessAdmin ? (
                            <NewArticleButton
                                categories={navCategories ?? []}
                                trigger={
                                    <button
                                        type="button"
                                        className={cn(
                                            headerIconClass(),
                                            'data-[state=open]:border-white/25 data-[state=open]:bg-white/[0.06] data-[state=open]:text-white',
                                        )}
                                        aria-label="כתבה חדשה"
                                        title="כתבה חדשה"
                                    >
                                        <Plus className="size-4" aria-hidden />
                                    </button>
                                }
                            />
                        ) : null}

                        {canAccessAdmin ? (
                            <Link
                                to={staffAdminHomePath(staffRole)}
                                className={cn(
                                    headerIconClass(activePath?.startsWith('/admin')),
                                    'hidden border-s-0 lg:inline-flex',
                                )}
                                aria-label="ניהול"
                                title="ניהול"
                            >
                                <LayoutDashboard className="size-4" aria-hidden />
                            </Link>
                        ) : null}

                        <Link
                            to="/search"
                            className={cn(
                                headerIconClass(activePath === '/search'),
                                canAccessAdmin && 'border-s-0',
                            )}
                            aria-label="חיפוש"
                        >
                            <Search className="size-4" aria-hidden />
                        </Link>

                        <button
                            type="button"
                            className={cn(headerIconClass(open), 'border-s-0 lg:hidden')}
                            aria-label={open ? 'סגור תפריט' : 'פתח תפריט'}
                            aria-expanded={open}
                            aria-controls="mobile-site-nav"
                            onClick={() => setOpen((v) => !v)}
                        >
                            {open ? (
                                <X className="size-4" aria-hidden />
                            ) : (
                                <Menu className="size-4" aria-hidden />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <MobileNavMenu
                open={open}
                onOpenChange={setOpen}
                activePath={activePath}
                canAccessAdmin={canAccessAdmin}
                staffRole={staffRole}
            />
        </header>
    );
}

const FOOTER_LINK_CLASS =
    'text-sm text-white/55 transition-colors hover:text-white';

function FooterHeading({ children }: { children: React.ReactNode }) {
    return <p className="text-sm text-white">{children}</p>;
}

export function GridFooter() {
    const year = new Date().getFullYear();
    const navItems = useSiteNavItems();
    const sections = navItems.filter((item) => item.id !== 'home');
    const instagram = SITE_SOCIAL_LINKS.find((link) => link.id === 'instagram');
    const InstagramIcon = instagram?.icon;

    return (
        <footer>
            {/* Last Cell owns the seam crosses; do not also draw top Cross marks here. */}
            <div className="relative mx-auto max-w-7xl border-x border-white/10">
                <div className="relative grid items-stretch border-b border-white/10 sm:grid-cols-2">
                    <Cross className="-top-2.5 left-1/2 hidden -translate-x-1/2 sm:block" />
                    <Cross className="-bottom-2.5 left-1/2 hidden -translate-x-1/2 sm:block" />
                    <div className="h-full border-b border-white/10 px-6 py-8 sm:border-b-0 sm:border-e sm:border-white/10">
                        <NewsletterSignup
                            id="newsletter-email-footer"
                            stretch
                        />
                    </div>
                    {instagram && InstagramIcon ? (
                        <div className="flex h-full flex-col justify-between gap-4 px-6 py-8">
                            <div>
                                <p className="text-sm text-white">הישארו מעודכנים באינסטגרם</p>
                                <p className="mt-1.5 text-sm leading-relaxed text-white/55">
                                    קבלו דיווחים מהשטח ועדכונים חמים בעמוד האינסטגרם של אשקלון ניוז.
                                </p>
                            </div>
                            <a
                                href={instagram.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-10 w-fit shrink-0 items-center justify-center gap-2 border border-white/15 bg-white/[0.03] px-4 text-sm leading-none text-white transition-colors hover:border-white/30 hover:bg-white/[0.06]"
                            >
                                <InstagramIcon aria-hidden className="size-4 shrink-0" />
                                עקבו באינסטגרם
                            </a>
                        </div>
                    ) : (
                        <div className="px-6 py-8" aria-hidden />
                    )}
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-10 border-b border-white/10 px-6 py-12 md:grid-cols-12 md:gap-x-6 lg:gap-x-8 lg:py-14">
                    <div className="col-span-2 md:col-span-3">
                        <Link to="/" className="inline-flex shrink-0 items-center">
                            <img
                                src="/images/logo-footer-dark.svg"
                                alt="אשקלון ניוז"
                                className="h-8 w-auto"
                                width={120}
                                height={32}
                            />
                        </Link>
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/55">
                            מקור החדשות המוביל באשקלון מאז 2006. עדכונים מדויקים מהעיר ומהסביבה.
                        </p>
                        <ul className="mt-6 flex flex-wrap gap-2" aria-label="רשתות חברתיות">
                            {SITE_SOCIAL_LINKS.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <li key={link.id}>
                                        <a
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={link.label}
                                            className="inline-flex size-9 items-center justify-center border border-white/10 text-white/55 transition-colors hover:border-white/25 hover:text-white"
                                        >
                                            <Icon aria-hidden className="size-4" />
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <nav className="md:col-span-2" aria-label="מדורים">
                        <FooterHeading>מדורים</FooterHeading>
                        <ul className="mt-4 space-y-2.5">
                            {sections.map((item) => {
                                const match = item.href.match(/^\/category\/(.+)$/);
                                return (
                                    <li key={item.id}>
                                        {match ? (
                                            <Link
                                                to="/category/$key"
                                                params={{ key: match[1] }}
                                                className={FOOTER_LINK_CLASS}
                                            >
                                                {item.label}
                                            </Link>
                                        ) : (
                                            <Link
                                                to={item.href as '/'}
                                                className={FOOTER_LINK_CLASS}
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    <nav className="md:col-span-2" aria-label="גלה">
                        <FooterHeading>גלה</FooterHeading>
                        <ul className="mt-4 space-y-2.5">
                            <li>
                                <Link to="/" className={FOOTER_LINK_CLASS}>
                                    ראשי
                                </Link>
                            </li>
                            <li>
                                <Link to="/weather" className={FOOTER_LINK_CLASS}>
                                    מזג האוויר
                                </Link>
                            </li>
                            {CINEMA_PUBLIC_ENABLED ? (
                                <li>
                                    <Link to="/cinema" className={FOOTER_LINK_CLASS}>
                                        קולנוע
                                    </Link>
                                </li>
                            ) : null}
                            <li>
                                <Link to="/newsletter" className={FOOTER_LINK_CLASS}>
                                    ניוזלטר
                                </Link>
                            </li>
                            <li>
                                <Link to="/tip" className={FOOTER_LINK_CLASS}>
                                    שלחו ידיעה
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    <nav className="md:col-span-2" aria-label="מידע">
                        <FooterHeading>מידע</FooterHeading>
                        <ul className="mt-4 space-y-2.5">
                            <li>
                                <Link to="/about" className={FOOTER_LINK_CLASS}>
                                    אודות
                                </Link>
                            </li>
                            <li>
                                <Link to="/tip" className={FOOTER_LINK_CLASS}>
                                    שלחו ידיעה
                                </Link>
                            </li>
                            <li>
                                <Link to="/accessibility" className={FOOTER_LINK_CLASS}>
                                    הצהרת נגישות
                                </Link>
                            </li>
                            <li>
                                <a href="/sitemap.xml" className={FOOTER_LINK_CLASS}>
                                    מפת האתר
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/rss.xml"
                                    className={FOOTER_LINK_CLASS}
                                    type="application/rss+xml"
                                >
                                    RSS
                                </a>
                            </li>
                        </ul>
                    </nav>

                    <nav className="md:col-span-3" aria-label="עקבו">
                        <FooterHeading>עקבו</FooterHeading>
                        <ul className="mt-4 space-y-2.5">
                            {SITE_SOCIAL_LINKS.map((link) => (
                                <li key={link.id}>
                                    <a
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={FOOTER_LINK_CLASS}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <div className="flex flex-col items-center gap-3 px-6 py-6 text-center text-xs text-white/55 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:text-start">
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 sm:justify-start">
                        <span>© {year} אשקלון ניוז</span>
                        <span aria-hidden className="hidden text-white/20 sm:inline">
                            ·
                        </span>
                        <span>כל הזכויות שמורות</span>
                    </div>
                    <a
                        href="https://appwrite.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        dir="ltr"
                        lang="en"
                        className="inline-flex items-center justify-center gap-1.5 transition-colors hover:text-white"
                    >
                        <span>Powered and hosted by</span>
                        <img
                            src="/images/appwrite.svg"
                            alt="Appwrite"
                            className="h-3.5 w-auto opacity-70"
                            width={77}
                            height={14}
                        />
                    </a>
                </div>
            </div>
        </footer>
    );
}

export function GridShell({
    activePath,
    children,
}: {
    activePath?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col overscroll-none bg-black text-[#ededed] antialiased">
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-50 focus:border focus:border-white/25 focus:bg-black focus:px-4 focus:py-2 focus:text-sm focus:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
            >
                דלג לתוכן הראשי
            </a>
            <GridNav activePath={activePath} />
            <main id="main-content" tabIndex={-1} className="outline-none">
                {children}
            </main>
            <GridFooter />
        </div>
    );
}
